import React, { useMemo } from 'react';
import { Stack, Text, Group, Badge, ScrollArea, ActionIcon, Tooltip, Divider } from '@mantine/core';
import { Calendar, CheckCircle } from 'lucide-react';
import { getCompactRowStyle } from './compactRowStyles';
import { useDashboardData } from '../../../../hooks/dashboard/useDashboardData';
import { useUpdateTodo } from '../../../../hooks/useTodos';
import { formatFriendlyDate, getDaysUntil } from '../../../../utils/dashboard';
import { useNavigation } from '../../../../contexts/NavigationContext';
import { useIsTouch } from './useIsTouch';
import { useIsSmallScreen } from './useIsSmallScreen';

interface UpcomingDeadlinesProps {
  className?: string;
  onEditTodo?: (todoId: number) => void;
}

export const UpcomingDeadlines: React.FC<UpcomingDeadlinesProps> = ({ className, onEditTodo }) => {
  const dashboardData = useDashboardData();
  const isTouch = useIsTouch();
  const isSmall = useIsSmallScreen();
  const showTooltips = !(isTouch && isSmall);
  const updateTodoMutation = useUpdateTodo();
  useNavigation(); // retained hook call if future side-effects

  // Analyze upcoming deadlines (next 7 days)
  const upcomingAnalysis = useMemo(() => {
    const now = new Date();
    const sevenDaysFromNow = new Date(now.getTime() + 7 * 24 * 60 * 60 * 1000);
    
    // Get tasks with upcoming due dates
    const upcomingTasks = dashboardData.todos.filter(task => {
      if (!task.dueDate || task.isCompleted) return false;
      const dueDate = new Date(task.dueDate);
      return dueDate >= now && dueDate <= sevenDaysFromNow;
    });

    // Group by day
    const tasksByDay: Record<string, typeof upcomingTasks> = {};
    const next7Days: Array<{ date: Date; dateStr: string; dayName: string }> = [];
    
    for (let i = 0; i < 7; i++) {
      const date = new Date(now.getTime() + i * 24 * 60 * 60 * 1000);
      const dateStr = date.toISOString().split('T')[0];
      const dayName = formatFriendlyDate(date);
      
      next7Days.push({ date, dateStr, dayName });
      tasksByDay[dateStr] = upcomingTasks.filter(task => {
        if (!task.dueDate) return false;
        return new Date(task.dueDate).toISOString().split('T')[0] === dateStr;
      });
    }

    // Sort tasks by priority and time
    const sortedTasks = upcomingTasks.sort((a, b) => {
      // First by due date
      const aDays = getDaysUntil(a.dueDate!);
      const bDays = getDaysUntil(b.dueDate!);
      if (aDays !== bDays) return aDays - bDays;
      
      // Then by priority
      const priorityOrder = { High: 3, Medium: 2, Low: 1 };
      const aPriority = priorityOrder[a.priority];
      const bPriority = priorityOrder[b.priority];
      if (aPriority !== bPriority) return bPriority - aPriority;
      
      // Finally by time if available
      if (a.dueTime && b.dueTime) {
        return new Date(a.dueDate!).getTime() - new Date(b.dueDate!).getTime();
      }
      
      return 0;
    });

    // Priority breakdown
    const highPriority = upcomingTasks.filter(t => t.priority === 'High').length;
    const mediumPriority = upcomingTasks.filter(t => t.priority === 'Medium').length;
    const lowPriority = upcomingTasks.filter(t => t.priority === 'Low').length;

    // Time-based breakdown
    const today = upcomingTasks.filter(t => getDaysUntil(t.dueDate!) === 0).length;
    const tomorrow = upcomingTasks.filter(t => getDaysUntil(t.dueDate!) === 1).length;
    const thisWeek = upcomingTasks.filter(t => {
      const days = getDaysUntil(t.dueDate!);
      return days >= 2 && days <= 6;
    }).length;

    // Workload distribution (tasks per day)
    const dailyWorkload = next7Days.map(day => ({
      ...day,
      tasks: tasksByDay[day.dateStr],
      taskCount: tasksByDay[day.dateStr].length,
      highPriorityCount: tasksByDay[day.dateStr].filter(t => t.priority === 'High').length
    }));

    return {
      total: upcomingTasks.length,
      sortedTasks: sortedTasks.slice(0, 10), // Top 10 most urgent
      priorityBreakdown: { high: highPriority, medium: mediumPriority, low: lowPriority },
      timeBreakdown: { today, tomorrow, thisWeek },
      dailyWorkload,
      busiestDay: dailyWorkload.reduce((max, day) => 
        day.taskCount > max.taskCount ? day : max, 
        dailyWorkload[0]
      ),
      averageDaily: upcomingTasks.length / 7
    };
  }, [dashboardData.todos]);


  const getTimeUrgencyColor = (daysUntil: number) => {
    if (daysUntil === 0) return 'red';
    if (daysUntil === 1) return 'orange';
    if (daysUntil <= 3) return 'yellow';
    return 'blue';
  };

  const handleCompleteTask = async (taskId: number, e: React.MouseEvent) => {
    e.stopPropagation();
    try {
      await updateTodoMutation.mutateAsync({
        id: taskId,
        data: { isCompleted: true }
      });
    } catch (error) {
      console.error('Failed to complete task:', error);
    }
  };

  // Calendar view navigation removed in simplified version

  if (upcomingAnalysis.total === 0) {
    return (
      <Stack gap="sm" className={className}>
        <Group gap={6}>
          <Calendar size={16} style={{ color: 'var(--mantine-color-blue-6)' }} />
          <Text size="sm" fw={600}>This Week</Text>
          <Badge variant="light" color="green" size="xs">Clear</Badge>
        </Group>
        <Text size="xs" c="dimmed">No upcoming deadlines</Text>
      </Stack>
    );
  }

  return (
    <Stack gap="xs" className={className}>
      <Group gap={6} wrap="nowrap">
        <Calendar size={16} style={{ color: 'var(--mantine-color-blue-6)' }} />
        <Text size="sm" fw={600}>This Week</Text>
        {showTooltips ? (
          <Tooltip label="Total tasks due in next 7 days" withArrow>
            <Badge variant="filled" color="blue" size="xs">{upcomingAnalysis.total}</Badge>
          </Tooltip>
        ) : (
          <Badge variant="filled" color="blue" size="xs">{upcomingAnalysis.total}</Badge>
        )}
      </Group>
      <Group gap={8} wrap="nowrap">
        {upcomingAnalysis.timeBreakdown.today > 0 && (
          showTooltips ? (
            <Tooltip label="Due today" withArrow>
              <Badge size="xs" color="red" variant="light">Today {upcomingAnalysis.timeBreakdown.today}</Badge>
            </Tooltip>
          ) : (
            <Badge size="xs" color="red" variant="light">Today {upcomingAnalysis.timeBreakdown.today}</Badge>
          )
        )}
        {upcomingAnalysis.timeBreakdown.tomorrow > 0 && (
          showTooltips ? (
            <Tooltip label="Due tomorrow" withArrow>
              <Badge size="xs" color="orange" variant="light">Tomorrow {upcomingAnalysis.timeBreakdown.tomorrow}</Badge>
            </Tooltip>
          ) : (
            <Badge size="xs" color="orange" variant="light">Tomorrow {upcomingAnalysis.timeBreakdown.tomorrow}</Badge>
          )
        )}
  {/* Week badge removed per user request */}
      </Group>
  <ScrollArea h={210} offsetScrollbars>
        <Stack gap={4}>
          {upcomingAnalysis.sortedTasks.slice(0,5).map(task => {
            const daysUntil = getDaysUntil(task.dueDate!);
            const timeUrgencyColor = getTimeUrgencyColor(daysUntil);
            return (
              <Group
                key={task.id}
                gap={6}
                wrap="nowrap"
                className="mini-row upcoming-row"
                data-dark-style
                style={{
                  ...getCompactRowStyle(timeUrgencyColor),
                  border: 'none',
                  // Darker tint for better contrast in light mode
                  background: `var(--mantine-color-${timeUrgencyColor}-2)`,
                  color: 'var(--mantine-color-dark-9)',
                  // Supply dark mode background var only (not forcing light mode)
                  ['--row-bg' as any]: 'var(--mantine-color-dark-6)',
                  boxShadow: '0 1px 2px rgba(0,0,0,0.06)',
                  cursor: 'pointer'
                }}
                onClick={() => onEditTodo?.(task.id)}
              >
                {showTooltips ? (
                  <Tooltip label={daysUntil===0 ? 'Due today' : daysUntil===1 ? 'Due tomorrow' : `Due in ${daysUntil} days`} withArrow>
                    <Badge size="xs" variant="light" radius="sm" color={timeUrgencyColor}>
                      {daysUntil === 0 ? 'Today' : daysUntil === 1 ? 'Tmrrw' : `${daysUntil}d`}
                    </Badge>
                  </Tooltip>
                ) : (
                  <Badge size="xs" variant="light" radius="sm" color={timeUrgencyColor}>
                    {daysUntil === 0 ? 'Today' : daysUntil === 1 ? 'Tmrrw' : `${daysUntil}d`}
                  </Badge>
                )}
                <Text size="xs" fw={500} lineClamp={1} style={{ flex:1 }}>{task.title}</Text>
                <ActionIcon size="xs" variant="subtle" color="green" onClick={(e: React.MouseEvent)=>handleCompleteTask(task.id,e)} loading={updateTodoMutation.isPending}
                  style={{ background:'transparent' }}
                >
                  <CheckCircle size={12} />
                </ActionIcon>
              </Group>
            );
          })}
        </Stack>
      </ScrollArea>
      {isTouch && isSmall && (
        <>
          <Divider my={4} />
          <Group gap={4} wrap="wrap" style={{ fontSize:10, lineHeight:1.2, alignItems:'center' }} c="dimmed">
            <Text size="10px" fw={500}>Legend:</Text>
            <Group gap={4} style={{ alignItems:'center' }}>
              <span style={{ width:10, height:10, background:'var(--mantine-color-red-6)', borderRadius:2, display:'inline-block' }} />
              <Text size="10px">Today</Text>
            </Group>
            <Group gap={4} style={{ alignItems:'center' }}>
              <span style={{ width:10, height:10, background:'var(--mantine-color-orange-6)', borderRadius:2, display:'inline-block' }} />
              <Text size="10px">Tomorrow</Text>
            </Group>
            <Group gap={4} style={{ alignItems:'center' }}>
              <span style={{ width:10, height:10, background:'var(--mantine-color-yellow-5)', borderRadius:2, display:'inline-block' }} />
              <Text size="10px">Soon</Text>
            </Group>
            <Group gap={4} style={{ alignItems:'center' }}>
              <span style={{ width:10, height:10, background:'var(--mantine-color-blue-6)', borderRadius:2, display:'inline-block' }} />
              <Text size="10px">Later</Text>
            </Group>
          </Group>
        </>
      )}
    </Stack>
  );
};